import { useMemo } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";

interface UseDataNormalizationProps<T extends BaseEntity> {
  entitiesQueryRaw: any;
  enableFTS: boolean;
  finalData: T[];
  ftsTotalCount: number;
  filterContext: any;
  displayData: T[];
}

/**
 * Normalizes query results to a consistent shape
 * Handles: nodes vs data property, totalCount, pageInfo
 */
export function useDataNormalization<T extends BaseEntity>({
  entitiesQueryRaw,
  enableFTS,
  finalData,
  ftsTotalCount,
  filterContext,
  displayData,
}: UseDataNormalizationProps<T>) {
  // Data for export and pagination (normalized from query)
  const data = useMemo(() => {
    if (!entitiesQueryRaw?.data && !finalData && displayData.length === 0) return undefined;

    // Get current search term from filter context
    const currentSearchTerm = filterContext?.filters.search || "";

    // If FTS is enabled and we have a search term, use FTS results with FTS total count
    if (enableFTS && finalData && currentSearchTerm && currentSearchTerm.trim().length > 0) {
      return {
        data: finalData,
        totalCount: ftsTotalCount || finalData.length, // Use FTS total count for search results
        pageInfo: entitiesQueryRaw?.data?.pageInfo,
      };
    }

    // If FTS is enabled but NO search term, use GraphQL data with GraphQL total count
    // This ensures we show the correct total count for all contacts, not just the page size
    if (enableFTS && entitiesQueryRaw?.data) {
      if ("nodes" in entitiesQueryRaw.data) {
        return {
          data: displayData.length > 0 ? displayData : finalData || entitiesQueryRaw.data.nodes,
          totalCount: entitiesQueryRaw.data.totalCount, // Use GraphQL total count when not searching
          pageInfo: entitiesQueryRaw.data.pageInfo,
        };
      }
      // Normalize if it doesn't have nodes property
      return {
        data: displayData.length > 0 ? displayData : finalData || entitiesQueryRaw.data.data || [],
        totalCount: entitiesQueryRaw.data.totalCount,
        pageInfo: entitiesQueryRaw.data.pageInfo,
      };
    }

    // Otherwise normalize from query (FTS disabled)
    if (!entitiesQueryRaw?.data) return undefined;

    // Normalize to consistent shape
    if ("nodes" in entitiesQueryRaw.data) {
      return {
        data: displayData.length > 0 ? displayData : entitiesQueryRaw.data.nodes,
        totalCount: entitiesQueryRaw.data.totalCount,
        pageInfo: entitiesQueryRaw.data.pageInfo,
      };
    }
    // Normalize if it doesn't have nodes property
    return {
      data: displayData.length > 0 ? displayData : entitiesQueryRaw.data.data || [],
      totalCount: entitiesQueryRaw.data.totalCount,
      pageInfo: entitiesQueryRaw.data.pageInfo,
    };
  }, [entitiesQueryRaw?.data, enableFTS, finalData, ftsTotalCount, filterContext?.filters.search, displayData]);

  return { data };
}
